/**
 * Google Analytics 4 (GA4) utility for tracking user interactions
 * Enhanced tracking for RentaBahay real estate platform
 * Comprehensive button and user interaction tracking
 */

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// GA4 Measurement ID from index.html
const GA_MEASUREMENT_ID = 'G-QM844LQ8BT';

/**
 * Initialize Google Analytics if not already loaded
 */
export const initializeAnalytics = () => {
  if (typeof window !== 'undefined' && !window.gtag) {
    // Create gtag function if it doesn't exist
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };
    
    // Configure GA4
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_title: document.title,
      page_location: window.location.href,
    });
  }
};

/**
 * Track page views
 */
export const trackPageView = (pagePath: string, pageTitle?: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: pagePath,
      page_title: pageTitle || document.title,
    });
  }
};

/**
 * Track custom events
 */
export const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, {
      event_category: 'engagement',
      event_label: eventName,
      ...parameters,
    });
  }
};

/**
 * Landlord Dashboard Specific Tracking Functions
 */

// Navigation tracking
export const trackNavigation = (destination: string, source: string = 'landlord_dashboard') => {
  trackEvent('navigation_click', {
    event_category: 'navigation',
    destination,
    source,
    user_type: 'landlord',
  });
};

// Enhanced button click tracking for all user types
export const trackButtonClick = (buttonName: string, location: string, userType?: string, additionalData?: Record<string, any>) => {
  trackEvent('button_click', {
    event_category: 'user_interaction',
    button_name: buttonName,
    location,
    user_type: userType || 'guest',
    timestamp: new Date().toISOString(),
    page_url: window.location.href,
    page_title: document.title,
    ...additionalData,
  });
};

// Social media sharing tracking
export const trackSocialShare = (platform: string, contentType: string, contentId?: string, propertyId?: string) => {
  trackEvent('social_share', {
    event_category: 'engagement',
    platform,
    content_type: contentType,
    content_id: contentId,
    property_id: propertyId,
    timestamp: new Date().toISOString(),
  });
};

// Authentication actions tracking
export const trackAuthAction = (action: 'login' | 'logout' | 'register' | 'password_reset' | 'social_login', provider?: string, userType?: string) => {
  trackEvent('auth_action', {
    event_category: 'authentication',
    action,
    provider,
    user_type: userType,
    timestamp: new Date().toISOString(),
  });
};

// Property interaction tracking
export const trackPropertyInteraction = (action: string, propertyId: string, propertyType?: string, userType?: string) => {
  trackEvent('property_interaction', {
    event_category: 'property_engagement',
    action,
    property_id: propertyId,
    property_type: propertyType,
    user_type: userType || 'guest',
    timestamp: new Date().toISOString(),
  });
};

// Navigation tracking enhanced
export const trackNavigationEnhanced = (destination: string, source: string, userType?: string, method?: string) => {
  trackEvent('navigation_enhanced', {
    event_category: 'navigation',
    destination,
    source,
    user_type: userType || 'guest',
    method: method || 'click',
    timestamp: new Date().toISOString(),
  });
};

// Form interaction tracking
export const trackFormInteraction = (formName: string, action: 'start' | 'submit' | 'error' | 'abandon', fieldName?: string, userType?: string) => {
  trackEvent('form_interaction', {
    event_category: 'form_engagement',
    form_name: formName,
    action,
    field_name: fieldName,
    user_type: userType || 'guest',
    timestamp: new Date().toISOString(),
  });
};

// Modal and popup tracking
export const trackModalInteraction = (modalName: string, action: 'open' | 'close' | 'submit', userType?: string) => {
  trackEvent('modal_interaction', {
    event_category: 'ui_interaction',
    modal_name: modalName,
    action,
    user_type: userType || 'guest',
    timestamp: new Date().toISOString(),
  });
};

// Filter and search tracking enhanced
export const trackSearchEnhanced = (searchTerm: string, filterData?: Record<string, any>, resultsCount?: number, userType?: string, location?: string) => {
  trackEvent('search_enhanced', {
    event_category: 'search',
    search_term: searchTerm,
    filters: filterData ? JSON.stringify(filterData) : undefined,
    results_count: resultsCount,
    user_type: userType || 'guest',
    location: location || 'search_page',
    timestamp: new Date().toISOString(),
  });
};

// Contact and communication tracking
export const trackContactAction = (action: string, contactType: string, propertyId?: string, recipientType?: string, userType?: string) => {
  trackEvent('contact_action', {
    event_category: 'communication',
    action,
    contact_type: contactType,
    property_id: propertyId,
    recipient_type: recipientType,
    user_type: userType || 'guest',
    timestamp: new Date().toISOString(),
  });
};

// Download and file interaction tracking
export const trackFileInteraction = (action: 'download' | 'view' | 'upload', fileName: string, fileType: string, userType?: string) => {
  trackEvent('file_interaction', {
    event_category: 'content_engagement',
    action,
    file_name: fileName,
    file_type: fileType,
    user_type: userType || 'guest',
    timestamp: new Date().toISOString(),
  });
};

// Wishlist and favorites tracking
export const trackWishlistAction = (action: 'add' | 'remove' | 'view', propertyId: string, userType?: string) => {
  trackEvent('wishlist_action', {
    event_category: 'engagement',
    action,
    property_id: propertyId,
    user_type: userType || 'guest',
    timestamp: new Date().toISOString(),
  });
};

// Pagination and listing interactions
export const trackPaginationAction = (action: 'next' | 'previous' | 'page_jump', currentPage: number, totalPages: number, listingType?: string) => {
  trackEvent('pagination_action', {
    event_category: 'navigation',
    action,
    current_page: currentPage,
    total_pages: totalPages,
    listing_type: listingType,
    timestamp: new Date().toISOString(),
  });
};

// Property management actions
export const trackPropertyAction = (action: 'create' | 'edit' | 'delete' | 'view' | 'status_change', propertyId?: string, additionalData?: Record<string, any>) => {
  trackEvent('property_action', {
    event_category: 'property_management',
    action,
    property_id: propertyId,
    user_type: 'landlord',
    ...additionalData,
  });
};

// Dashboard feature usage
export const trackDashboardFeature = (feature: string, action: string, value?: string | number) => {
  trackEvent('dashboard_feature', {
    event_category: 'dashboard_usage',
    feature,
    action,
    value,
    user_type: 'landlord',
  });
};

// Financial tracking (revenue, payments, etc.)
export const trackFinancialAction = (action: string, amount?: number, currency: string = 'PHP', additionalData?: Record<string, any>) => {
  trackEvent('financial_action', {
    event_category: 'financial',
    action,
    value: amount,
    currency,
    user_type: 'landlord',
    ...additionalData,
  });
};

// Quick action tracking
export const trackQuickAction = (actionType: 'add_property' | 'create_maintenance' | 'view_messages' | 'generate_report') => {
  trackEvent('quick_action', {
    event_category: 'quick_actions',
    action_type: actionType,
    user_type: 'landlord',
  });
};

// Search and filter tracking
export const trackSearch = (searchTerm: string, filters?: Record<string, any>, resultsCount?: number) => {
  trackEvent('search', {
    event_category: 'search',
    search_term: searchTerm,
    filters: JSON.stringify(filters),
    results_count: resultsCount,
    user_type: 'landlord',
  });
};

// Error tracking
export const trackError = (errorType: string, errorMessage: string, location: string) => {
  trackEvent('error', {
    event_category: 'error',
    error_type: errorType,
    error_message: errorMessage,
    location,
    user_type: 'landlord',
  });
};

// Performance tracking
export const trackPerformance = (metric: string, value: number, page: string) => {
  trackEvent('performance', {
    event_category: 'performance',
    metric,
    value,
    page,
    user_type: 'landlord',
  });
};

// Enhanced performance tracking with detailed metrics
export const trackDetailedPerformance = (
  metric: string, 
  value: number, 
  page: string, 
  additionalData?: Record<string, any>
) => {
  trackEvent('performance', {
    event_category: 'performance',
    metric,
    value,
    page,
    user_type: 'landlord',
    ...additionalData,
  });
};

// Core Web Vitals tracking
export const trackCoreWebVitals = (
  lcp?: number,
  fid?: number,
  cls?: number,
  ttfb?: number,
  fcp?: number,
  page?: string
) => {
  const metrics: Record<string, any> = {
    event_category: 'core_web_vitals',
    page: page || window.location.pathname,
    user_type: 'landlord',
  };

  if (lcp !== undefined) metrics.lcp = Math.round(lcp);
  if (fid !== undefined) metrics.fid = Math.round(fid);
  if (cls !== undefined) metrics.cls = cls.toFixed(3);
  if (ttfb !== undefined) metrics.ttfb = Math.round(ttfb);
  if (fcp !== undefined) metrics.fcp = Math.round(fcp);

  trackEvent('core_web_vitals', metrics);
};

// User engagement tracking
export const trackEngagement = (engagementType: 'time_on_page' | 'scroll_depth' | 'click_depth', value: number, page: string) => {
  trackEvent('engagement', {
    event_category: 'engagement',
    engagement_type: engagementType,
    value,
    page,
    user_type: 'landlord',
  });
};

// Communication tracking
export const trackCommunication = (action: 'send_message' | 'receive_message' | 'view_conversation', recipientType?: 'tenant' | 'agent' | 'admin') => {
  trackEvent('communication', {
    event_category: 'communication',
    action,
    recipient_type: recipientType,
    user_type: 'landlord',
  });
};

/**
 * Enhanced tracking with automatic user context
 */
export class LandlordAnalytics {
  private static userId: string | null = null;
  private static userProperties: Record<string, any> = {};

  static setUser(userId: string, properties?: Record<string, any>) {
    this.userId = userId;
    this.userProperties = properties || {};
    
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', GA_MEASUREMENT_ID, {
        user_id: userId,
        custom_map: {
          user_type: 'landlord',
          ...properties,
        },
      });
    }
  }

  static trackWithContext(eventName: string, parameters?: Record<string, any>) {
    trackEvent(eventName, {
      user_id: this.userId,
      ...this.userProperties,
      ...parameters,
    });
  }

  static trackPropertyMetrics(metrics: {
    totalProperties: number;
    occupiedProperties: number;
    monthlyRevenue: number;
    occupancyRate: number;
  }) {
    this.trackWithContext('landlord_metrics', {
      event_category: 'business_metrics',
      total_properties: metrics.totalProperties,
      occupied_properties: metrics.occupiedProperties,
              price: metrics.monthlyRevenue,
      occupancy_rate: metrics.occupancyRate,
    });
  }

  static trackDashboardLoad(loadTime: number, dataComplete: boolean) {
    this.trackWithContext('dashboard_load', {
      event_category: 'performance',
      load_time_ms: loadTime,
      data_complete: dataComplete,
      page: 'landlord_dashboard',
    });
  }
}

// Defer stub/config work so importing this module does not lengthen the initial main-thread task (TBT).
if (typeof window !== 'undefined') {
  const run = () => initializeAnalytics();
  if ('requestIdleCallback' in window) {
    requestIdleCallback(run, { timeout: 5000 });
  } else {
    setTimeout(run, 0);
  }
}

export default {
  trackPageView,
  trackEvent,
  trackNavigation,
  trackButtonClick,
  trackSocialShare,
  trackAuthAction,
  trackPropertyInteraction,
  trackNavigationEnhanced,
  trackFormInteraction,
  trackModalInteraction,
  trackSearchEnhanced,
  trackContactAction,
  trackFileInteraction,
  trackWishlistAction,
  trackPaginationAction,
  trackPropertyAction,
  trackDashboardFeature,
  trackFinancialAction,
  trackQuickAction,
  trackSearch,
  trackError,
  trackPerformance,
  trackEngagement,
  trackCommunication,
  LandlordAnalytics,
};